import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * FUEL GAUGE
 *
 * A dash's fuel dial with a row of bars under it. The driver taps the bar that
 * matches the needle on the dash, and the dial on screen moves to match: a
 * picture of the thing they are reading, so they copy it instead of guessing
 * which word "three quarters" is.
 *
 * The bottom quarter of the tank is reserve. A reading in it turns the fill
 * warning, never with motion: the needle swings once when it is set, and
 * nothing pulses.
 */
export const fuelDialRecipe = cva([uiMarker, 'block h-auto w-full max-w-64']);

/** The dial's arcs: the empty track, the reserve, and the filled run. */
export const fuelArcRecipe = cva([uiMarker, 'fill-none'], {
  variants: {
    part: {
      track: 'stroke-muted',
      reserve: 'stroke-destructive/30',
      fill: 'transition-[stroke-dasharray] duration-base ease-out',
    },
    low: { true: '', false: '' },
  },
  compoundVariants: [
    { part: 'fill', low: false, className: 'stroke-primary' },
    { part: 'fill', low: true, className: 'stroke-warning' },
  ],
  defaultVariants: { part: 'track', low: false },
});

/** A tick on the dial. Ticks in the reserve are drawn red. */
export const fuelTickRecipe = cva([uiMarker], {
  variants: {
    reserve: {
      true: 'stroke-destructive',
      false: 'stroke-muted-foreground',
    },
  },
  defaultVariants: { reserve: false },
});

/** The letters and the half mark printed on the dial face. */
export const fuelDialTextRecipe = cva([uiMarker, 'text-caption-strong'], {
  variants: {
    tone: {
      empty: 'fill-destructive',
      full: 'fill-foreground',
      muted: 'fill-muted-foreground',
    },
  },
  defaultVariants: { tone: 'muted' },
});

/**
 * The needle. It rotates by an inline style, since the angle is a runtime
 * value; this only says how it moves and how it looks before a reading.
 */
export const fuelNeedleRecipe = cva(
  [uiMarker, 'transition-transform duration-slow ease-out'],
  {
    variants: {
      set: { true: '', false: 'opacity-40' },
    },
    defaultVariants: { set: true },
  }
);

export const fuelNeedleLineRecipe = cva([uiMarker, 'stroke-destructive']);
export const fuelHubRecipe = cva([uiMarker, 'fill-foreground']);
export const fuelHubDotRecipe = cva([uiMarker, 'fill-card']);

/** The row the bars sit in. */
export const fuelBarsRecipe = cva([
  uiMarker,
  'flex h-control-lg items-stretch gap-1',
]);

/**
 * One bar. The first is the empty stop, a square with an E in it; the rest
 * fill up to the reading.
 */
export const fuelBarRecipe = cva(
  [
    uiMarker,
    'rounded-md border transition-colors duration-fast',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ],
  {
    variants: {
      kind: {
        empty:
          'flex w-control-lg shrink-0 items-center justify-center text-caption-strong',
        level: 'flex-1',
      },
      state: {
        off: '',
        on: '',
        low: '',
      },
    },
    compoundVariants: [
      {
        kind: 'empty',
        state: 'off',
        className:
          'border-input bg-background text-muted-foreground hover:bg-muted',
      },
      {
        kind: 'empty',
        state: ['on', 'low'],
        className: 'border-destructive bg-destructive-subtle text-destructive',
      },
      {
        kind: 'level',
        state: 'off',
        className: 'border-transparent bg-muted hover:bg-muted-foreground/20',
      },
      { kind: 'level', state: 'on', className: 'border-primary bg-primary' },
      { kind: 'level', state: 'low', className: 'border-warning bg-warning' },
    ],
    defaultVariants: { kind: 'level', state: 'off' },
  }
);

/** The scale under the bars: 1/4, 1/2, 3/4, F. */
export const fuelScaleRecipe = cva([uiMarker, 'flex gap-1']);
export const fuelScaleMarkRecipe = cva([uiMarker, 'flex-1 text-right'], {
  variants: {
    spacer: { true: 'w-control-lg flex-none', false: '' },
  },
  defaultVariants: { spacer: false },
});

export type FuelBarRecipeProps = VariantProps<typeof fuelBarRecipe>;
